/**
 * BaaS accounts via MobileService `/v1/ib/console/*` → Baas (`v1/accounts`, balances, statements).
 */

import {
  resolveAccountOperationalStatus,
  resolveProposalSituation,
} from "../Utils/baasProposalSituation";
import { baasProxy } from "./baasConsoleClient";
import { fetchBaasEntities, fetchBaasEntityById } from "./baasEntidadesApi";
import {
  isVirtualInstanceShellAccount,
  isVirtualInstanceShellEntity,
  isBaasSegregationWalletAccount,
  isBaasExitRailVirtualAccount,
} from "./baasVirtualShells";
import { fetchInternalTransferOptions } from "./baasInternalTransfersApi";
import { fetchBaasVirtualAccounts } from "./baasVirtualAccountsApi";
import intl from "react-intl-universal";
import { formatMonetaryValue } from "../Utils/formatters";

export const BAAS_ACCOUNT_LIST_STATUSES = ["active", "blocked", "pending", "suspended", "deactivated"];
export const BAAS_ACCOUNT_LIST_TYPES = [
  "operational",
  "settlement",
  "reserve",
  "overdraft",
  "program",
  "root",
  "wallet",
  "otc",
  "origin_rail",
];
/** Account types allowed when creating — excludes root/pinned accounts. */
export const BAAS_ACCOUNT_CREATABLE_TYPES = ["operational", "settlement", "reserve"];
/** Virtual segregation wallet (any instance with Root; PaySmart via Root bolsão). */
export const BAAS_ACCOUNT_WALLET_TYPE = "wallet";
/** UI alias for WALLET / segregation virtual account. */
export const BAAS_ACCOUNT_VIRTUAL_TYPE = "virtual";
/** Valut-only OTC exit-rail virtual for now. */
export const BAAS_ACCOUNT_OTC_TYPE = "otc";

const VIRTUAL_UI_TYPES = new Set([
  BAAS_ACCOUNT_WALLET_TYPE,
  BAAS_ACCOUNT_VIRTUAL_TYPE,
  "segregation_wallet",
]);

const OTC_UI_TYPES = new Set([BAAS_ACCOUNT_OTC_TYPE, "otc_virtual"]);

export const isBaasVirtualAccountType = (type) =>
  VIRTUAL_UI_TYPES.has(String(type || "").trim().toLowerCase());

export const isBaasOtcAccountType = (type) =>
  OTC_UI_TYPES.has(String(type || "").trim().toLowerCase());

/**
 * Creatable Contas types for the operational "Criar conta" flow.
 * Virtual create uses WALLET only via BaasVirtualAccountCreateModal (no OTC / shells).
 */
export const getBaasAccountCreatableTypes = () => [
  ...BAAS_ACCOUNT_CREATABLE_TYPES,
];

/**
 * True only for the Valut matrix / global-admin program (VALUT-GLOBAL), not
 * client programs that happen to include "VALUT" in the name (e.g. VALUT-SAUDE).
 * Also accepts explicit AllowGlobalAccess from the catalog when present.
 */
export const isValutSegregationWalletInstance = (instance = {}) => {
  if (
    instance?.allowGlobalAccess === true ||
    instance?.allow_global_access === true
  ) {
    return true;
  }

  const candidates = [
    instance?.displayName,
    instance?.name,
    instance?.programId,
    instance?.programCode,
    instance?.ownerEntityId,
  ];

  return candidates.some((value) => {
    const normalized = String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s*\(HML\)\s*$/i, "")
      .replace(/[\s_]+/g, "-");
    if (!normalized) return false;
    // Exact / prefix match for VALUT-GLOBAL (program code or display name).
    if (normalized === "VALUT-GLOBAL" || normalized.startsWith("VALUT-GLOBAL-")) {
      return true;
    }
    // Compact forms: valutglobal, VALUTGLOBALLLC, etc.
    const compact = normalized.replace(/-/g, "");
    return compact === "VALUTGLOBAL" || compact.startsWith("VALUTGLOBAL");
  });
};

/** Legacy chip list (includes stables). Prefer getBaasAccountCreatableCurrencies. */
export const BAAS_ACCOUNT_CURRENCIES = ["BRL", "USD", "EUR", "GBP", "USDC", "USDT"];

/** Fiat rails the Contas create modal can open today (country → currency). */
export const BAAS_ACCOUNT_FIAT_CURRENCIES = ["BRL", "USD", "EUR", "GBP"];

/**
 * Fiat currencies available on the instance for Criar conta.
 * Prefers instance.supportedCurrencies (or supported_currencies); falls back to BRL/USD.
 */
export const getBaasAccountCreatableCurrencies = (instance = {}) => {
  const raw = instance?.supportedCurrencies ?? instance?.supported_currencies ?? [];
  const supported = (Array.isArray(raw) ? raw : [])
    .map((code) => String(code || "").trim().toUpperCase())
    .filter(Boolean);
  const fiat = BAAS_ACCOUNT_FIAT_CURRENCIES.filter((code) => supported.includes(code));
  if (fiat.length > 0) return fiat;
  return ["BRL", "USD"];
};

/** Fiat currency → ISO country for Contas create / uniqueness checks. */
export const countryCodeFromCurrency = (currencyCode) => {
  const code = String(currencyCode || "")
    .trim()
    .toUpperCase();
  switch (code) {
    case "BRL":
      return "BR";
    case "USD":
      return "US";
    case "GBP":
      return "GB";
    case "EUR":
      return "EU";
    default:
      return null;
  }
};

/**
 * Previously blocked a second operational opening per currency/country.
 * Multi-account per instance is allowed (PaySmart Conta Digital), so this is a no-op.
 */
export const entityHasActiveCurrencyAccount = (
  accounts = [],
  entityId,
  currencyCode,
) => {
  void accounts;
  void entityId;
  void currencyCode;
  return false;
};

/** Currencies from `available` that the entity already has open — always empty (multi-account allowed). */
export const getEntityBlockedCurrencies = (
  accounts = [],
  entityId,
  availableCurrencies = [],
) => {
  void accounts;
  void entityId;
  void availableCurrencies;
  return [];
};

/** @deprecated Prefer entityHasActiveCurrencyAccount(..., "BRL") */
export const entityHasActiveBrAccount = (accounts = [], entityId) =>
  entityHasActiveCurrencyAccount(accounts, entityId, "BRL");

export {
  isVirtualInstanceShellAccount,
  isVirtualInstanceShellEntity,
  isBaasSegregationWalletAccount,
  isBaasExitRailVirtualAccount,
  isBaasOriginRailAccount,
  isPixKeyLinkableTargetAccount,
  isLedgerOnlyVirtualAccount,
  isContasOperationalTableExcludedShell,
  isBaasInstanceOwnerEntity,
  isBaasInstanceOwnerAccount,
  resolveBaasInstanceOwnerEntityId,
} from "./baasVirtualShells";
const mapAccountStatus = (verificationStatus) => {
  const status = String(verificationStatus || "").toUpperCase();
  if (status === "APPROVED" || status === "ACTIVE") return "active";
  if (status === "BLOCKED" || status === "SUSPENDED" || status === "REJECTED") return "blocked";
  return "pending";
};

const sanitizeAccountNumber = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.toLowerCase() === "root") return "";
  return text;
};

const mapAccountType = (accountType) => {
  const normalized = String(accountType || "").toUpperCase();
  if (normalized === "VIRTUAL_ROOT" || normalized === "VIRTUAL") return "virtual";
  if (normalized === "ROOT" || normalized === "ROOT ACCOUNT") return "root";
  if (normalized === "OPERATING") return "operational";
  if (normalized === "OVERDRAFT_RESERVE" || normalized === "OVERDRAFT") return "overdraft";
  if (normalized === "PROGRAM_RESERVE" || normalized === "PROGRAM") return "program";
  if (normalized === "OTC" || normalized === "OTC_VIRTUAL") return "otc";
  if (normalized === "ORIGIN_RAIL" || normalized === "ORIGIN RAIL") return "origin_rail";
  if (
    normalized === "WALLET" ||
    normalized === "SEGREGATION_WALLET" ||
    normalized.includes("SEGREGATION")
  ) {
    return "wallet";
  }
  if (normalized.includes("RESERVE")) return "reserve";
  if (normalized.includes("SETTLE")) return "settlement";
  return "operational";
};

/** Pinned role from internal-transfer-options → Contas UI type. */
export const mapPinnedRoleToAccountType = (role) => {
  const normalized = String(role || "").toLowerCase();
  if (normalized === "root") return "root";
  if (normalized === "operating") return "operational";
  if (normalized === "overdraft_reserve") return "overdraft";
  if (normalized === "program_reserve") return "program";
  if (normalized === "otc" || normalized === "otc_virtual") return "otc";
  if (normalized === "origin_rail") return "origin_rail";
  if (normalized === "segregation_wallet" || normalized === "wallet") return "wallet";
  return mapAccountType(role);
};

const mapBalances = (balanceResponse) => {
  const items = Array.isArray(balanceResponse?.items)
    ? balanceResponse.items
    : Array.isArray(balanceResponse)
      ? balanceResponse
      : [];

  return items
    .map((item) => {
      const currency = String(item.currency_code || item.currency || "").toUpperCase();
      if (!currency) return null;
      // BaaS BalanceList amounts are in minor units (cents for BRL). Limit is major or limit_cents.
      const allowOverdraft = Boolean(item.allow_overdraft ?? item.allowOverdraft);
      const available = Number(item.available ?? item.available_amount ?? 0) / 100;
      const blocked = Number(item.blocked ?? item.blocked_amount ?? item.held ?? 0) / 100;
      const future = Number(item.future ?? item.scheduled ?? item.scheduled_balance ?? 0) / 100;
      const limitCentsRaw = item.limit_cents ?? item.limitCents;
      const limit =
        limitCentsRaw != null && limitCentsRaw !== ""
          ? Number(limitCentsRaw) / 100
          : Number(item.limit ?? 0);
      const spendableRaw = item.spendable ?? item.Spendable;
      const spendable =
        spendableRaw != null && spendableRaw !== ""
          ? Number(spendableRaw) / 100
          : available + (allowOverdraft ? Number(limit) || 0 : 0);
      return {
        currency,
        available,
        blocked,
        future,
        limit: Number.isFinite(limit) ? limit : 0,
        allowOverdraft,
        spendable: Number.isFinite(spendable) ? spendable : available,
      };
    })
    .filter(Boolean);
};

/**
 * Stable key for an operating/BaaS account row.
 * Prefer BaaS account id (`acct_…` / banking.account_id); fall back to account number.
 */
export const getAccountStableId = (account) => {
  const id = account?.id != null ? String(account.id).trim() : "";
  const number =
    account?.accountNumber != null ? String(account.accountNumber).trim() : "";
  return id || number || "";
};

export const hasBaasBankingDetails = (banking = {}) =>
  Boolean(
    banking.bankLabel ||
      banking.bankCode ||
      banking.bankName ||
      banking.issuer ||
      banking.partnerCode ||
      banking.branch ||
      banking.accountNumber ||
      banking.ispb ||
      banking.routingNumber ||
      banking.bic ||
      banking.scheme,
  );

/**
 * Bank display name for Contas cards/rows: issuer (BankPartner.Name) like "JPMorgan Chase".
 * Skips placeholders ("ISSUER") and partner slugs (paysmart/jpmorgan); falls back to institution bank_name.
 */
export const getBaasBankDisplayName = (banking = {}) => {
  const issuer = banking.issuer ? String(banking.issuer).trim() : "";
  const bankName = banking.bankName ? String(banking.bankName).trim() : "";
  const bankLabel = banking.bankLabel ? String(banking.bankLabel).trim() : "";
  const partnerCode = banking.partnerCode ? String(banking.partnerCode).trim() : "";

  const isPlaceholder = (value) => {
    if (!value) return true;
    if (/^issuer$/i.test(value)) return true;
    // Never surface the processor (PaySmart) as the Contas bank / provider label.
    if (/paysmart/i.test(value)) return true;
    if (partnerCode && value.toLowerCase() === partnerCode.toLowerCase()) return true;
    return false;
  };

  if (!isPlaceholder(issuer)) return issuer;
  if (!isPlaceholder(bankName)) return bankName;
  if (bankLabel && !isPlaceholder(bankLabel)) return bankLabel;
  return "";
};

/** ISO country → currency when BaaS omits currency_code on an opening. */
export const currencyCodeFromCountry = (countryCode) => {
  const code = String(countryCode || "")
    .trim()
    .toUpperCase();
  switch (code) {
    case "BR":
      return "BRL";
    case "US":
      return "USD";
    case "GB":
      return "GBP";
    case "EU":
    case "DE":
    case "FR":
    case "IT":
    case "ES":
    case "PT":
    case "NL":
    case "IE":
    case "AT":
    case "BE":
    case "FI":
    case "LU":
      return "EUR";
    default:
      return null;
  }
};

const accountDedupeScore = (account) => {
  const id = String(account?.id || "");
  let score = 0;
  if (id.startsWith("acct_")) score += 4;
  else if (id) score += 2;
  if (account?.accountNumber) score += 1;
  if ((account?.balances || []).length) score += 1;
  if ((account?.currencyOpenings || []).length) score += (account.currencyOpenings.length > 1 ? 2 : 1);
  if (account?.entityName) score += 1;
  // Prefer peer Conta Digital rows (instance badge) when merging with weaker entity-list hits.
  if (account?.isInstanceRoot === true || account?.is_instance_root === true) score += 3;
  return score;
};

/** Union balance rows by currency (max per pot — same ledger, not two wallets). */
export const mergeBalanceLists = (left = [], right = []) => {
  const byCurrency = new Map();

  for (const balance of [...(left || []), ...(right || [])]) {
    const currency = String(balance?.currency || "").toUpperCase();
    if (!currency) continue;
    const next = {
      currency,
      available: Number(balance.available ?? 0),
      blocked: Number(balance.blocked ?? 0),
      future: Number(balance.future ?? 0),
      limit: Number(balance.limit ?? 0),
      allowOverdraft: Boolean(balance.allowOverdraft),
      spendable: Number(balance.spendable ?? balance.available ?? 0),
    };
    const previous = byCurrency.get(currency);
    if (!previous) {
      byCurrency.set(currency, next);
      continue;
    }
    byCurrency.set(currency, {
      currency,
      available: Math.max(previous.available, next.available),
      blocked: Math.max(previous.blocked, next.blocked),
      future: Math.max(previous.future, next.future),
      limit: Math.max(previous.limit || 0, next.limit || 0),
      allowOverdraft: previous.allowOverdraft || next.allowOverdraft,
      spendable: Math.max(previous.spendable || 0, next.spendable || 0),
    });
  }

  return [...byCurrency.values()];
};

/** Union per-currency openings; prefer the side that has banking details. */
export const mergeCurrencyOpeningLists = (left = [], right = []) => {
  const byCurrency = new Map();

  for (const opening of [...(left || []), ...(right || [])]) {
    const currency = String(opening?.currency || "").toUpperCase();
    if (!currency) continue;
    const next = { ...opening, currency };
    const previous = byCurrency.get(currency);
    if (!previous) {
      byCurrency.set(currency, next);
      continue;
    }
    const preferNext =
      hasBaasBankingDetails(next.banking) && !hasBaasBankingDetails(previous.banking);
    byCurrency.set(currency, preferNext ? next : previous);
  }

  return [...byCurrency.values()];
};

const STATUS_RANK = { blocked: 3, pending: 2, active: 1 };

/** Prefer non-active operational status so dedupe never resurrects "Ativo" over pending. */
const preferOperationalStatus = (left, right) => {
  const leftRank = STATUS_RANK[left] || 0;
  const rightRank = STATUS_RANK[right] || 0;
  return leftRank >= rightRank ? left || right : right || left;
};

const preferProposalSituation = (left, right) => {
  if (!left) return right || null;
  if (!right) return left;
  const leftPending = left.verificationStatus === "PENDING" || left.accountStatus === "pending"
    || left.verificationStatus === "REJECTED" || left.accountStatus === "blocked";
  const rightPending = right.verificationStatus === "PENDING" || right.accountStatus === "pending"
    || right.verificationStatus === "REJECTED" || right.accountStatus === "blocked";
  if (leftPending && !rightPending) return left;
  if (rightPending && !leftPending) return right;
  return left;
};

const mergeDedupedAccounts = (preferred, other) => {
  const preferredId = String(preferred?.id || "");
  const otherId = String(other?.id || "");
  const id = preferredId.startsWith("acct_")
    ? preferredId
    : otherId.startsWith("acct_")
      ? otherId
      : preferredId || otherId;

  const status = preferOperationalStatus(preferred.status, other.status);
  const proposalSituation = preferProposalSituation(
    preferred.proposalSituation,
    other.proposalSituation,
  );
  const documentPending =
    preferred.documentPending === true || other.documentPending === true;
  const antiFraudAnalysis =
    preferred.antiFraudAnalysis === true || other.antiFraudAnalysis === true;
  const canTransact =
    status === "active" && !documentPending && !antiFraudAnalysis
    && preferred.canTransact !== false
    && other.canTransact !== false;

  const isPeerRoot =
    preferred.isInstanceRoot === true || other.isInstanceRoot === true || false;

  return {
    ...preferred,
    id,
    accountNumber: preferred.accountNumber || other.accountNumber || "",
    entityName: preferred.entityName || other.entityName || "",
    entityId: preferred.entityId || other.entityId || "",
    entityType: preferred.entityType || other.entityType || null,
    countryCode: preferred.countryCode || other.countryCode || null,
    currencyCode: preferred.currencyCode || other.currencyCode || null,
    // Prefer exit-rail type/role from either side so Contas virtuais keeps WALLET/OTC
    // when a weaker entity-list row won the dedupe score.
    // Peer Conta Digital stays ENTITY | CONTA (operational), never INFRA Root type.
    type: isPeerRoot ? "operational" : preferExitRailType(preferred, other),
    kind: preferred.kind || other.kind || null,
    status,
    proposalSituation,
    documentPending,
    antiFraudAnalysis,
    statusName: preferred.statusName || other.statusName || null,
    canTransact,
    isVirtual:
      preferred.isVirtual === true ||
      preferred.is_virtual === true ||
      other.isVirtual === true ||
      other.is_virtual === true ||
      undefined,
    is_virtual:
      preferred.is_virtual === true ||
      preferred.isVirtual === true ||
      other.is_virtual === true ||
      other.isVirtual === true ||
      undefined,
    balances: mergeBalanceLists(preferred.balances, other.balances),
    currencyOpenings: mergeCurrencyOpeningLists(
      preferred.currencyOpenings,
      other.currencyOpenings,
    ),
    banking: hasBaasBankingDetails(preferred.banking)
      ? preferred.banking
      : other.banking || preferred.banking,
    createdAt: preferred.createdAt || other.createdAt || null,
    metadata: preferred.metadata?.externalAccountId
      ? preferred.metadata
      : other.metadata || preferred.metadata,
    instanceId: preferred.instanceId ?? other.instanceId ?? null,
    instanceName: preferred.instanceName || other.instanceName || null,
    programCode: preferred.programCode || other.programCode || null,
    isInstanceRoot: isPeerRoot,
    is_root: isPeerRoot
      ? false
      : preferred.is_root === true || other.is_root === true || false,
    role: isPeerRoot ? null : preferred.role || other.role || null,
  };
};

const preferExitRailType = (preferred, other) => {
  const types = [preferred?.type, other?.type, preferred?.kind, other?.kind]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  if (types.includes("otc") || types.includes("otc_virtual")) return "otc";
  if (
    types.includes("wallet") ||
    types.includes("virtual") ||
    types.includes("segregation_wallet")
  ) {
    return "wallet";
  }
  return preferred?.type || other?.type;
};

/**
 * Collapse duplicate account rows produced by per-entity `v1/accounts` fetches
 * (same account can appear under more than one entity query, and multi-currency
 * list responses return one OpenAccount row per currency for the same acct id).
 * Merges balances + currency openings so detail can show every rail for that id.
 */
export const dedupeAccountsByStableId = (accounts = []) => {
  const byStableId = new Map();
  const stableIdByNumber = new Map();

  for (const account of accounts) {
    if (!account) continue;

    const id = account.id != null ? String(account.id).trim() : "";
    const number =
      account.accountNumber != null ? String(account.accountNumber).trim() : "";
    const stableId = id || number;
    if (!stableId) continue;

    const existingKey =
      (byStableId.has(stableId) ? stableId : null) ||
      (number ? stableIdByNumber.get(number) : null);

    const candidate = {
      ...account,
      id: stableId,
      accountNumber: number || account.accountNumber || "",
      currencyOpenings: Array.isArray(account.currencyOpenings)
        ? account.currencyOpenings
        : [],
    };

    if (!existingKey) {
      byStableId.set(stableId, candidate);
      if (number) stableIdByNumber.set(number, stableId);
      continue;
    }

    const previous = byStableId.get(existingKey);
    const preferred =
      accountDedupeScore(candidate) > accountDedupeScore(previous) ? candidate : previous;
    const other = preferred === candidate ? previous : candidate;
    const winner = mergeDedupedAccounts(preferred, other);

    if (String(winner.id) !== existingKey) {
      byStableId.delete(existingKey);
    }
    byStableId.set(String(winner.id), winner);
    const winnerNumber = String(winner.accountNumber || number || "").trim();
    if (winnerNumber) stableIdByNumber.set(winnerNumber, String(winner.id));
  }

  return Array.from(byStableId.values());
};

/** Map BaaS `banking` object (OpenAPI Banking) onto a stable UI shape. */
export const mapBaasBankingToUi = (banking = {}) => {
  const bankCode = banking.bank_code ? String(banking.bank_code).trim() : "";
  const bankName = banking.bank_name ? String(banking.bank_name).trim() : "";
  const accountNumber = banking.account_number ? String(banking.account_number).trim() : "";
  const issuer = banking.issuer ? String(banking.issuer).trim() : "";
  const partnerCode = banking.partner_code
    ? String(banking.partner_code).trim()
    : banking.partnerCode
      ? String(banking.partnerCode).trim()
      : "";

  return {
    accountId: banking.account_id ? String(banking.account_id).trim() : "",
    accountNumber,
    scheme: banking.scheme ? String(banking.scheme).trim() : "",
    bankCode,
    bankName,
    /** BankPartner.Name — bank display name (UI label: Banco / Bank). */
    issuer,
    /** BankPartner.PartnerCode (e.g. paysmart) — integration code, not bank name. */
    partnerCode,
    /** Fallback label when issuer/bank_name absent: bank name or COMPE/code. */
    bankLabel: bankName || bankCode || "",
    ispb: banking.ispb != null && String(banking.ispb).trim() ? String(banking.ispb).trim() : "",
    branch: banking.branch != null && String(banking.branch).trim() ? String(banking.branch).trim() : "",
    routingNumber:
      banking.routing_number != null && String(banking.routing_number).trim()
        ? String(banking.routing_number).trim()
        : "",
    bic: banking.bic != null && String(banking.bic).trim() ? String(banking.bic).trim() : "",
  };
};

export const mapBaasAccountToUi = (
  item,
  { entityName = "", entityType = null, instanceId = null, balances = [] } = {},
) => {
  const bankingRaw = item?.banking ?? {};
  const banking = mapBaasBankingToUi(bankingRaw);
  const rawAccountId = banking.accountId || item?.account_id || item?.id;
  const accountNumber = sanitizeAccountNumber(
    banking.accountNumber || item?.account_number || item?.accountNumber,
  );
  const accountId = rawAccountId != null && String(rawAccountId).trim()
    ? String(rawAccountId).trim()
    : accountNumber;
  const countryCode = item?.country_code || item?.countryCode || null;
  const currency = String(
    item?.currency_code ||
      item?.currencyCode ||
      currencyCodeFromCountry(countryCode) ||
      "",
  ).toUpperCase();

  const resolvedBalances =
    balances.length > 0
      ? balances
      : currency
        ? [{ currency, available: 0, blocked: 0, future: 0 }]
        : [];

  const currencyOpenings = currency
    ? [
        {
          currency,
          countryCode,
          accountType: item?.account_type || item?.accountType || null,
          verificationStatus: item?.verification_status || item?.verificationStatus || null,
          banking,
        },
      ]
    : [];

  const resolvedEntityType = entityType
    ? String(entityType).toLowerCase()
    : null;

  const mappedType = mapAccountType(item?.account_type);
  const isPeerInstanceRoot =
    item?.is_instance_root === true || item?.isInstanceRoot === true || false;
  const scheme = String(banking.scheme || "").toLowerCase();
  const isExitRail =
    mappedType === "wallet" ||
    mappedType === "otc" ||
    scheme === "virtual_segregation" ||
    scheme === "virtual_otc";

  const proposalSituation = resolveProposalSituation(
    item?.proposal_situation ?? item?.proposalSituation,
    item,
  );
  const proposalIdRaw = item?.proposal_id ?? item?.proposalId ?? null;
  const proposalId =
    proposalIdRaw != null && proposalIdRaw !== "" ? Number(proposalIdRaw) : null;
  const documentPending = Boolean(
    item?.document_pending ?? item?.documentPending ?? false,
  );
  const antiFraudAnalysis = Boolean(
    item?.anti_fraud_analysis ?? item?.antiFraudAnalysis ?? false,
  );
  const statusNameRaw = item?.status_name ?? item?.statusName ?? null;
  const statusName =
    statusNameRaw != null && String(statusNameRaw).trim()
      ? String(statusNameRaw).trim()
      : null;

  // Prefer proposal_situation / Account flags when present (open-account + list);
  // otherwise fall back to entity verification_status.
  const operational = resolveAccountOperationalStatus({
    proposalSituation,
    verificationStatus: item?.verification_status ?? item?.verificationStatus,
    documentPending,
    antiFraudAnalysis,
    statusName,
    raw: item,
    lifecycle: item?.lifecycle ?? item?.Lifecycle ?? null,
  });
  const status = operational.status || mapAccountStatus(item?.verification_status);

  return {
    id: accountId,
    accountNumber,
    alias: accountNumber || accountId,
    status,
    // Peer Conta Digital on matriz Contas = ENTITY | CONTA (not INFRA Root type).
    type: isPeerInstanceRoot ? "operational" : mappedType,
    entityId: item?.entity_id || "",
    entityName: entityName || item?.entity_name || item?.entity_id || "",
    /** `pf` = person/sócio; `pj` = business entity. Used to keep PF out of operacional cards. */
    entityType: resolvedEntityType,
    instanceId:
      item?.instance_id != null && item?.instance_id !== ""
        ? Number(item.instance_id)
        : instanceId,
    instanceName: String(item?.instance_name || "").trim() || null,
    programCode: String(item?.program_code || item?.programCode || "").trim() || null,
    createdAt:
      item?.created_at
      || item?.createdAt
      || item?.register_date
      || item?.registerDate
      || null,
    countryCode,
    currencyCode: currency || null,
    balances: resolvedBalances,
    /** Per-currency openings from list (one OpenAccount row per rail); merged on dedupe. */
    currencyOpenings,
    /** Bank / payment-account refs from BaaS `banking`. */
    banking,
    /** Present after POST open-account; list GET usually omits. */
    proposalId: Number.isFinite(proposalId) ? proposalId : null,
    proposalSituation: operational.proposalSituation || proposalSituation,
    documentPending,
    antiFraudAnalysis,
    statusName,
    canTransact: operational.canTransact === true,
    verificationStatus: item?.verification_status || item?.verificationStatus || null,
    lifecycle: operational.status === "suspended" || operational.status === "deactivated" || operational.status === "blocked"
      ? operational.status
      : (item?.lifecycle || item?.Lifecycle || null),
    closedAccount: Boolean(item?.closed_account ?? item?.closedAccount ?? false),
    active: item?.active !== false && item?.Active !== false,
    metadata: {
      externalAccountId: banking.accountId || "",
      ledgerAccountId: "",
      // Issuer / COMPE institution name — never PaySmart (processor) or partner_code.
      providerRef: getBaasBankDisplayName(banking),
      scheme: banking.scheme || "",
      bankCode: banking.bankCode || "",
      bankName: banking.bankName || "",
      issuer: banking.issuer || "",
      partnerCode: banking.partnerCode || "",
      branch: banking.branch || "",
      ispb: banking.ispb || "",
      routingNumber: banking.routingNumber || "",
      bic: banking.bic || "",
    },
    is_root: !isPeerInstanceRoot && mappedType === "root",
    isInstanceRoot: isPeerInstanceRoot,
    role: isPeerInstanceRoot ? null : item?.role || null,
    pinned: false,
    /** Ledger-only exit-rail virtual (WALLET / OTC — not an instance OPERATING shell). */
    is_virtual: isExitRail,
    isVirtual: isExitRail,
    raw: item,
  };
};

/**
 * Entity CONTAS E WALLETS / GET ?entity_id= — only rows that open on Contas detail.
 * Drops closed/deactivated shells and accounts GET /v1/accounts/{id} would 404.
 */
export const isOpenableEntityRelatedAccount = (account) => {
  if (!account?.id) return false;
  if (isVirtualInstanceShellAccount(account)) return false;
  if (account.closedAccount === true) return false;
  const status = String(account.status || account.lifecycle || "").toLowerCase();
  if (status === "deactivated" || status === "closed") return false;
  return true;
};

export const fetchAccountBalances = async (instance, accountId) => {
  if (!instance || !accountId) return [];

  try {
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: `v1/accounts/${encodeURIComponent(accountId)}/balances`,
    });
    return mapBalances(data);
  } catch {
    return [];
  }
};

const CURRENCY_VIEW_ORDER = ["BRL", "USD", "EUR", "GBP", "USDC", "USDT"];
/** Stablecoin wallet assets — belong to stables wallets, not account fiat rails. */
const STABLES_WALLET_ONLY_CURRENCIES = new Set(["BRL1", "EURC"]);

const sortCurrencyCodes = (a, b) => {
  const indexA = CURRENCY_VIEW_ORDER.indexOf(a);
  const indexB = CURRENCY_VIEW_ORDER.indexOf(b);
  const rankA = indexA === -1 ? CURRENCY_VIEW_ORDER.length : indexA;
  const rankB = indexB === -1 ? CURRENCY_VIEW_ORDER.length : indexB;
  if (rankA !== rankB) return rankA - rankB;
  return a.localeCompare(b);
};

/**
 * One OpenAccount-shaped item per currency opening from
 * `GET /v1/accounts/{id}/currencies` (AccountData + banking).
 */
export const mapBaasCurrencyOpeningToUi = (item) => {
  const countryCode = item?.country_code || item?.countryCode || null;
  const currency = String(
    item?.currency_code ||
      item?.currencyCode ||
      item?.currency ||
      currencyCodeFromCountry(countryCode) ||
      "",
  ).toUpperCase();
  if (!currency) return null;

  return {
    currency,
    countryCode,
    accountType: item?.account_type || item?.accountType || null,
    verificationStatus: item?.verification_status || item?.verificationStatus || null,
    entityId: item?.entity_id || item?.entityId || null,
    banking: mapBaasBankingToUi(item?.banking ?? {}),
    raw: item,
  };
};

/**
 * Currency openings + banking via console proxy → BaaS
 * `GET /v1/accounts/{accountId}/currencies`.
 */
export const fetchAccountCurrencies = async (instance, accountId) => {
  if (!instance || !accountId) return [];

  try {
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: `v1/accounts/${encodeURIComponent(accountId)}/currencies`,
    });
    const items = Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];
    return items.map(mapBaasCurrencyOpeningToUi).filter(Boolean);
  } catch {
    return [];
  }
};

/**
 * Merge BalanceList + currency openings into one UI row per currency.
 * Banking prefers currencies API (per AccountData); falls back to account-level
 * banking only when currencies returned no openings at all.
 */
export const mergeBalancesWithCurrencyOpenings = (
  balances = [],
  currencyOpenings = [],
  fallbackBanking = null,
) => {
  const byCurrency = new Map();

  for (const balance of Array.isArray(balances) ? balances : []) {
    const currency = String(balance?.currency || "").toUpperCase();
    if (!currency || STABLES_WALLET_ONLY_CURRENCIES.has(currency)) continue;
    byCurrency.set(currency, {
      currency,
      available: Number(balance.available ?? 0),
      blocked: Number(balance.blocked ?? 0),
      future: Number(balance.future ?? 0),
      limit: Number(balance.limit ?? 0),
      allowOverdraft: Boolean(balance.allowOverdraft),
      spendable: Number(balance.spendable ?? balance.available ?? 0),
      banking: null,
      countryCode: null,
      hasOpening: false,
    });
  }

  for (const opening of Array.isArray(currencyOpenings) ? currencyOpenings : []) {
    const currency = String(opening?.currency || "").toUpperCase();
    if (!currency || STABLES_WALLET_ONLY_CURRENCIES.has(currency)) continue;
    const existing = byCurrency.get(currency) || {
      currency,
      available: 0,
      blocked: 0,
      future: 0,
      limit: 0,
      allowOverdraft: false,
      spendable: 0,
      banking: null,
      countryCode: null,
      hasOpening: false,
    };
    existing.banking = opening.banking ?? null;
    existing.countryCode = opening.countryCode ?? null;
    existing.hasOpening = true;
    byCurrency.set(currency, existing);
  }

  const openingsEmpty = !currencyOpenings?.length;
  if (openingsEmpty && hasBaasBankingDetails(fallbackBanking)) {
    for (const row of byCurrency.values()) {
      if (!hasBaasBankingDetails(row.banking)) {
        row.banking = fallbackBanking;
      }
    }
  }

  return [...byCurrency.values()].sort((a, b) => sortCurrencyCodes(a.currency, b.currency));
};

/** BaaS `ListAccountStatementsQueryValidator`: InclusiveBetween(1, 100). */
export const BAAS_STATEMENT_PAGE_SIZE = 100;
/** Max rows console Extrato / Transações aggregates across BaaS pages. */
export const MAX_BAAS_STATEMENT_ITEMS = 5000;

/**
 * Normalize console statement query keys to BaaS snake_case and clamp page_size.
 * Accepts camelCase aliases (pageSize, startDate, endDate, currencyCode, fetchAll).
 */
export const normalizeBaasStatementsQuery = (query = {}) => {
  const rawPageSize = Number(query.page_size ?? query.pageSize);
  const wantsAll =
    Boolean(query.fetch_all ?? query.fetchAll) ||
    (Number.isFinite(rawPageSize) && rawPageSize > BAAS_STATEMENT_PAGE_SIZE);

  const pageSize = wantsAll
    ? BAAS_STATEMENT_PAGE_SIZE
    : Math.min(
        BAAS_STATEMENT_PAGE_SIZE,
        Math.max(1, Number.isFinite(rawPageSize) ? rawPageSize : BAAS_STATEMENT_PAGE_SIZE),
      );

  const normalized = {
    page: Math.max(1, Number(query.page) || 1),
    page_size: pageSize,
  };

  const startDate = query.start_date ?? query.startDate;
  const endDate = query.end_date ?? query.endDate;
  if (startDate) normalized.start_date = startDate;
  if (endDate) normalized.end_date = endDate;

  const type = query.type;
  if (type && String(type).toLowerCase() !== "all") normalized.type = type;

  const currency = query.currency_code ?? query.currencyCode;
  if (currency) normalized.currency_code = String(currency).toUpperCase();

  return { normalized, wantsAll };
};

/**
 * Account statements via MobileService console proxy → BaaS
 * `GET /v1/accounts/{accountId}/statements`.
 *
 * When `fetch_all` / `pageSize` > 100, pages in chunks of 100 until exhausted
 * or {@link MAX_BAAS_STATEMENT_ITEMS}.
 */
export const fetchAccountStatements = async (instance, accountId, query = {}) => {
  if (!instance || !accountId) return { items: [], total: 0, page: 1, page_size: BAAS_STATEMENT_PAGE_SIZE };

  const { normalized, wantsAll } = normalizeBaasStatementsQuery(query);
  const baasPath = `v1/accounts/${encodeURIComponent(accountId)}/statements`;

  if (!wantsAll) {
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath,
      query: normalized,
    });

    return {
      items: Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [],
      total: Number(data?.total ?? 0),
      page: Number(data?.page ?? normalized.page),
      page_size: Number(data?.page_size ?? normalized.page_size),
      raw: data,
    };
  }

  const maxItems = Math.min(
    MAX_BAAS_STATEMENT_ITEMS,
    Math.max(1, Number(query.max_items ?? query.maxItems) || MAX_BAAS_STATEMENT_ITEMS),
  );
  const pageSize = normalized.page_size;
  const items = [];
  let total = Infinity;
  let page = 1;
  let lastPayload = {};
  const maxPages = Math.ceil(maxItems / pageSize);

  while (page <= maxPages && items.length < total && items.length < maxItems) {
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath,
      query: { ...normalized, page, page_size: pageSize },
    });
    const batch = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
    total = Number(data?.total ?? batch.length);
    items.push(...batch);
    lastPayload = data || {};
    if (batch.length < pageSize) break;
    page += 1;
  }

  return {
    items: items.slice(0, maxItems),
    total: Number(lastPayload?.total ?? items.length),
    page: 1,
    page_size: pageSize,
    raw: lastPayload,
  };
};

const fetchAccountsForEntity = async (instance, entity, { includeBalances = false } = {}) => {
  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: "v1/accounts",
    query: { entity_id: entity.id },
  });

  const items = Array.isArray(data?.items) ? data.items : [];

  // Multi-currency / multi-saldo accounts arrive as one OpenAccount row per
  // currency with the same acct_ id. Collapse to one Conta before UI lists.
  const mapped = dedupeAccountsByStableId(
    items.map((item) =>
      mapBaasAccountToUi(item, {
        entityName: entity.name,
        entityType: entity.type,
        instanceId: instance?.id,
        balances: [],
      }),
    ),
  ).filter(isOpenableEntityRelatedAccount);

  // Default: metadata-only list (no per-account GET …/balances). Operational
  // saldo for IB/console detail stays on the selected account only.
  if (!includeBalances) {
    return mapped;
  }

  return Promise.all(
    mapped.map(async (account) => {
      const accountId = account?.id;
      const balances = accountId ? await fetchAccountBalances(instance, accountId) : [];
      return {
        ...account,
        balances: mergeBalanceLists(balances, account.balances),
      };
    }),
  );
};

/**
 * GET `v1/accounts?entity_id={id}` — accounts for a single entity.
 * Prefer this over instance-wide `fetchBaasAccounts` on entity detail.
 * Dedupes by stable acct_ id / account number so multi-saldo rails are one Conta.
 * Drops closed/deactivated/shells so CONTAS E WALLETS matches click-through GET by id.
 *
 * @param {object} instance
 * @param {{ id: string, name?: string, type?: string }} entity
 * @param {{ includeBalances?: boolean }} [options]
 */
export const fetchBaasAccountsForEntity = async (instance, entity, options = {}) => {
  if (!instance || !entity?.id) return [];
  return fetchAccountsForEntity(instance, entity, {
    includeBalances: options.includeBalances === true,
  });
};

/**
 * Valut matrix only: GET `v1/accounts/peer-roots` — Conta Digital (RootAccountId) of
 * other instances for Contas as ENTITY | CONTA (instance_name badge). Empty for
 * partners / non-matrix pins. Never merge these into INFRA / Shells operacionais.
 *
 * @param {object} instance
 */
export const fetchBaasPeerInstanceRoots = async (instance, options = {}) => {
  if (!instance || !isValutSegregationWalletInstance(instance)) return [];

  try {
    const query = {};
    if (options.instanceId != null && Number.isFinite(Number(options.instanceId))) {
      query.instance_id = Number(options.instanceId);
    }
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: "v1/accounts/peer-roots",
      query,
    });
    const items = Array.isArray(data?.items) ? data.items : [];
    return dedupeAccountsByStableId(
      items.map((item) => {
        const entityName = item?.entity_name || item?.entityName || "";
        const mapped = mapBaasAccountToUi(item, {
          entityName,
          instanceId: item?.instance_id ?? item?.instanceId ?? null,
          balances: [],
        });
        // Contas row: ENTITY | CONTA — keep account number as Conta, not a Root shell card.
        mapped.isInstanceRoot = true;
        mapped.is_root = false;
        mapped.role = null;
        mapped.type = "virtual";
        mapped.isVirtual = true;
        mapped.is_virtual = true;
        if (entityName) {
          mapped.entityName = entityName;
        }
        if (
          !mapped.alias
          || mapped.alias === mapped.id
          || (entityName && mapped.alias === entityName)
        ) {
          mapped.alias = mapped.accountNumber || mapped.id;
        }
        if (!mapped.name || (entityName && mapped.name === entityName)) {
          mapped.name = mapped.alias || mapped.accountNumber || mapped.id;
        }
        return mapped;
      }),
    );
  } catch (error) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[baas] GET /v1/accounts/peer-roots failed", error);
    }
    return [];
  }
};

/**
 * @param {object} instance
 * @param {{ entities?: array, includeBalances?: boolean, failOnVirtualsError?: boolean, onVirtualsError?: (error: unknown) => void }} [options]
 * `includeBalances` defaults to false — avoid N balance calls on every list.
 * `failOnVirtualsError` rethrows when GET /v1/accounts/virtuals fails (default: soft-fail).
 * `onVirtualsError` is called on soft-fail so Contas can toast without emptying the whole list.
 */
export const fetchBaasAccounts = async (instance, options = {}) => {
  if (!instance) return [];

  const includeBalances = options.includeBalances === true;
  const rawEntities =
    options.entities ??
    (await fetchBaasEntities(instance).catch(() => []));
  const entities = (Array.isArray(rawEntities) ? rawEntities : []).filter(
    (entity) => !isVirtualInstanceShellEntity(entity),
  );

  const [entityChunks, virtualsSettled] = await Promise.all([
    entities.length
      ? Promise.all(
          entities.map((entity) =>
            fetchAccountsForEntity(instance, entity, { includeBalances }).catch(() => []),
          ),
        )
      : Promise.resolve([]),
    fetchBaasVirtualAccounts(instance)
      .then((result) => ({ ok: true, result }))
      .catch((error) => ({ ok: false, error, result: { items: [] } })),
  ]);

  if (!virtualsSettled.ok) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        "[baas] GET /v1/accounts/virtuals failed — Contas virtuais may be empty",
        virtualsSettled.error,
      );
    }
    if (typeof options.onVirtualsError === "function") {
      options.onVirtualsError(virtualsSettled.error);
    }
    if (options.failOnVirtualsError === true) {
      throw virtualsSettled.error;
    }
  }

  // Merge GET /v1/accounts/virtuals (WALLET + OTC with exit bank partner).
  // Peer instance virtual roots belong on Contas virtuais — not here.
  return dedupeAccountsByStableId([
    ...entityChunks.flat(),
    ...(virtualsSettled.result?.items || []),
  ]).filter(
    (account) =>
      account?.isInstanceRoot !== true && !isVirtualInstanceShellAccount(account),
  );
};

const attachRelatedAccountsWithBalances = async (
  instance,
  detail,
  { accounts = [], transferOptions = {} } = {},
) => {
  const related = resolveRelatedVirtualAccounts({
    currentAccount: detail,
    accounts,
    transferOptions,
    rootAccountId: instance?.rootAccountId,
  });
  const [operationAccounts, virtualAccounts] = await Promise.all([
    enrichRelatedAccountsWithBalances(instance, related.operationAccounts),
    enrichRelatedAccountsWithBalances(instance, related.virtualAccounts),
  ]);
  return {
    ...detail,
    operationAccounts,
    virtualAccounts,
  };
};

export const fetchBaasAccountById = async (instance, accountId) => {
  if (!instance || !accountId) return null;

  const [accounts, balances, openings, transferOptions, entities] = await Promise.all([
    fetchBaasAccounts(instance),
    fetchAccountBalances(instance, accountId),
    fetchAccountCurrencies(instance, accountId),
    fetchInternalTransferOptions(instance).catch(() => ({
      sourceAccounts: [],
      destinationAccounts: [],
      virtualAccounts: [],
    })),
    fetchBaasEntities(instance).catch(() => []),
  ]);

  const found = accounts.find((account) => account.id === accountId);
  const listOpenings = found?.currencyOpenings ?? [];
  const mergedOpenings = mergeCurrencyOpeningLists(openings, listOpenings);
  const mergedBalances = mergeBalanceLists(
    balances,
    found?.balances ?? [],
  );

  const pinnedMatch = [
    ...(transferOptions.sourceAccounts || []),
    ...(transferOptions.destinationAccounts || []),
    ...(transferOptions.virtualAccounts || []),
  ].find((account) => account?.id === accountId);

  const openingEntityId =
    mergedOpenings.find((opening) => opening?.entityId)?.entityId ||
    mergedOpenings.find((opening) => opening?.raw?.entity_id)?.raw?.entity_id ||
    mergedOpenings.find((opening) => opening?.raw?.entityId)?.raw?.entityId ||
    "";

  const entityId =
    found?.entityId ||
    pinnedMatch?.entityId ||
    openingEntityId ||
    "";

  const entityFromList = (Array.isArray(entities) ? entities : []).find(
    (entity) => entity?.id === entityId,
  );
  let entityName =
    found?.entityName ||
    entityFromList?.name ||
    "";
  let entityType =
    found?.entityType ||
    (entityFromList?.type ? String(entityFromList.type).toLowerCase() : null);

  // Owner entity may be missing from the filtered entities list — resolve by id.
  if (entityId && !entityName) {
    const resolved = await fetchBaasEntityById(instance, entityId).catch(() => null);
    if (resolved?.name) entityName = resolved.name;
    if (!entityType && resolved?.type) entityType = String(resolved.type).toLowerCase();
  }
  if (!entityName) entityName = entityId || "";

  if (found) {
    const detail = {
      ...found,
      entityId: entityId || found.entityId || "",
      entityName: entityName || found.entityName || "",
      entityType: entityType || found.entityType || null,
      type: pinnedMatch?.role
        ? mapPinnedRoleToAccountType(pinnedMatch.role)
        : found.type,
      role: pinnedMatch?.role || found.role || null,
      pinned: Boolean(pinnedMatch) || found.pinned === true,
      is_root:
        pinnedMatch?.role === "root" ||
        found.is_root === true ||
        found.isRoot === true,
      isRoot:
        pinnedMatch?.role === "root" ||
        found.is_root === true ||
        found.isRoot === true,
      is_virtual: pinnedMatch?.isVirtual === true || found.is_virtual === true,
      balances: mergedBalances.length ? mergedBalances : found.balances || [],
      currencyOpenings: mergedOpenings,
    };
    // Programs list cards omit rootAccountId — recover from transfer-options Root pin.
    const resolvedRootAccountId =
      instance?.rootAccountId ||
      transferOptions.sourceAccounts?.find(
        (account) => String(account?.role || "").toLowerCase() === "root",
      )?.id ||
      null;
    return attachRelatedAccountsWithBalances(
      { ...instance, rootAccountId: resolvedRootAccountId },
      detail,
      {
        accounts,
        transferOptions,
      },
    );
  }

  // Pinned virtual shells (OPERATING / OVERDRAFT_RESERVE / PROGRAM_RESERVE) are
  // excluded from per-entity v1/accounts — hydrate from transfer-options + balances.
  if (pinnedMatch) {
    const primaryCurrency =
      (mergedBalances[0] && mergedBalances[0].currency) ||
      (pinnedMatch.balances?.[0] && pinnedMatch.balances[0].currency) ||
      null;
    const openingBanking = mergedOpenings.find((row) => hasBaasBankingDetails(row.banking))
      ?.banking;

    const detail = {
      id: accountId,
      accountNumber: accountId,
      alias: pinnedMatch.name || accountId,
      name: pinnedMatch.name || accountId,
      status: "active",
      type: mapPinnedRoleToAccountType(pinnedMatch.role),
      role: pinnedMatch.role || null,
      entityId,
      entityName,
      entityType,
      instanceId: instance?.id,
      createdAt: null,
      countryCode: mergedOpenings[0]?.countryCode || null,
      currencyCode: primaryCurrency,
      balances: mergedBalances.length
        ? mergedBalances
        : pinnedMatch.balances || [],
      currencyOpenings: mergedOpenings,
      banking: openingBanking || mapBaasBankingToUi({ account_id: accountId }),
      metadata: {
        externalAccountId: accountId,
      },
      is_root: pinnedMatch.role === "root",
      isRoot: pinnedMatch.role === "root",
      pinned: true,
      is_virtual: pinnedMatch.isVirtual === true,
      isVirtual: pinnedMatch.isVirtual === true,
    };
    const resolvedRootAccountId =
      instance?.rootAccountId ||
      transferOptions.sourceAccounts?.find(
        (account) => String(account?.role || "").toLowerCase() === "root",
      )?.id ||
      (pinnedMatch.role === "root" ? accountId : null);
    return attachRelatedAccountsWithBalances(
      { ...instance, rootAccountId: resolvedRootAccountId },
      detail,
      {
        accounts,
        transferOptions,
      },
    );
  }

  const fallback = {
    id: accountId,
    accountNumber: accountId,
    alias: accountId,
    status: "active",
    type: mapAccountType(
      mergedOpenings[0]?.accountType || mergedOpenings[0]?.raw?.account_type,
    ),
    entityId,
    entityName,
    entityType,
    instanceId: instance?.id,
    createdAt: null,
    countryCode: mergedOpenings[0]?.countryCode || null,
    currencyCode: null,
    balances: mergedBalances,
    currencyOpenings: mergedOpenings,
    banking: mapBaasBankingToUi({}),
    metadata: {},
  };
  // Peer-root / matrix GET-by-id can succeed without a local list row.
  // Closed leftovers (no InstanceAccounts) 404 currencies+balances — do not invent a ghost.
  if (mergedOpenings.length === 0 && mergedBalances.length === 0) {
    return null;
  }
  return attachRelatedAccountsWithBalances(instance, fallback, {
    accounts,
    transferOptions,
  });
};

/** Display order for related Shells operacionais (Root first) — mirrors Contas cards. */
const RELATED_OPERATION_SHELL_ROLE_ORDER = {
  root: 0,
  operating: 1,
  overdraft_reserve: 2,
  overdraft: 2,
  program_reserve: 3,
  program: 3,
  otc: 4,
  otc_virtual: 4,
  reserve: 5,
};

/**
 * Related accounts for the account being viewed, split by kind:
 * - operationAccounts: instance shells (Root + OPERATING / OVERDRAFT / PROGRAM)
 * - virtualAccounts: exit-rail virtuals (WALLET + OTC)
 *
 * On Root detail, Root itself is included (subtitle lists Root). Balances come from
 * transfer-options and/or the accounts list; callers may fetch missing balances.
 */
export const resolveRelatedVirtualAccounts = ({
  currentAccount,
  accounts = [],
  transferOptions = {},
  rootAccountId = null,
} = {}) => {
  const currentId = String(currentAccount?.id || "").trim();
  if (!currentId) {
    return { operationAccounts: [], virtualAccounts: [] };
  }

  const entityId = String(currentAccount?.entityId || "").trim();
  const currentRole = String(
    currentAccount?.role || currentAccount?.type || "",
  )
    .trim()
    .toLowerCase();
  const instanceRootId = String(rootAccountId || "").trim();
  const isRootView =
    currentRole === "root" ||
    currentAccount?.is_root === true ||
    currentAccount?.isRoot === true ||
    (instanceRootId !== "" && instanceRootId === currentId);
  const currentIsOperationShell =
    isRootView ||
    (!isBaasExitRailVirtualAccount(currentAccount) &&
      (["operating", "overdraft_reserve", "program_reserve"].includes(currentRole) ||
        isVirtualInstanceShellAccount(currentAccount) ||
        currentAccount?.isVirtual === true ||
        currentAccount?.is_virtual === true));

  const listById = new Map();
  for (const account of Array.isArray(accounts) ? accounts : []) {
    const id = String(account?.id || "").trim();
    if (id && !listById.has(id)) listById.set(id, account);
  }

  const withListBalances = (account) => {
    const id = String(account?.id || "").trim();
    const fromList = listById.get(id);
    if (!fromList) return account;
    return {
      ...fromList,
      ...account,
      balances: mergeBalanceLists(account?.balances, fromList?.balances),
      currencyOpenings: mergeCurrencyOpeningLists(
        account?.currencyOpenings,
        fromList?.currencyOpenings,
      ),
    };
  };

  const operationById = new Map();
  const virtualById = new Map();

  const pushOperation = (account, { allowCurrent = false } = {}) => {
    const id = String(account?.id || "").trim();
    if (!id) return;
    if (!allowCurrent && id === currentId) return;
    const enriched = withListBalances(account);
    if (operationById.has(id)) {
      const previous = operationById.get(id);
      operationById.set(id, {
        ...previous,
        ...enriched,
        balances: mergeBalanceLists(enriched?.balances, previous?.balances),
      });
      return;
    }
    operationById.set(id, enriched);
  };

  const pushVirtual = (account) => {
    const id = String(account?.id || "").trim();
    if (!id || id === currentId) return;
    const enriched = withListBalances(account);
    if (virtualById.has(id)) {
      const previous = virtualById.get(id);
      virtualById.set(id, {
        ...previous,
        ...enriched,
        isVirtual: true,
        is_virtual: true,
        balances: mergeBalanceLists(enriched?.balances, previous?.balances),
      });
      return;
    }
    virtualById.set(id, {
      ...enriched,
      isVirtual: true,
      is_virtual: true,
    });
  };

  for (const account of Array.isArray(accounts) ? accounts : []) {
    if (!isBaasExitRailVirtualAccount(account)) continue;
    // Root is the exit rail for every WALLET/OTC on this instance — show them all,
    // regardless of which client entity owns the wallet.
    if (isRootView) {
      pushVirtual(account);
      continue;
    }
    if (entityId && String(account.entityId || "").trim() !== entityId) continue;
    // When entity is unknown, still surface wallets from the list cautiously — skip.
    if (!entityId) continue;
    pushVirtual(account);
  }

  const transferPool = [
    ...(transferOptions.destinationAccounts || []),
    ...(transferOptions.virtualAccounts || []),
    ...(transferOptions.sourceAccounts || []),
  ];

  for (const account of transferPool) {
    const role = String(account?.role || "").toLowerCase();
    if (isBaasExitRailVirtualAccount(account)) {
      if (isRootView || !entityId || String(account.entityId || "").trim() === entityId) {
        pushVirtual(account);
      }
      continue;
    }

        // Instance Root (RootAccountId / sourceAccounts role=root) — show with other shells
    // when the current account is itself part of the operational shell set.
    // Skip peer Conta Digitals (other instances' RootAccountId on matrix AccInst).
    // When callers omit rootAccountId (programs cards), recover from sourceAccounts pin.
    const resolvedInstanceRootId =
      instanceRootId
      || String(
        (transferOptions.sourceAccounts || []).find(
          (row) => String(row?.role || "").toLowerCase() === "root",
        )?.id || "",
      ).trim();
    if (role === "root") {
      const accountId = String(account?.id || "").trim();
      if (resolvedInstanceRootId && accountId && accountId !== resolvedInstanceRootId) {
        continue;
      }
      if (!resolvedInstanceRootId) {
        // Ambiguous: do not promote destination peer Conta Digitals into shells.
        continue;
      }
      if (currentIsOperationShell) {
        // On Root detail include self; elsewhere keep siblings only.
        pushOperation(account, { allowCurrent: isRootView });
      }
      continue;
    }

    const isShell =
      account?.isVirtual === true ||
      ["operating", "overdraft_reserve", "program_reserve"].includes(role);
    if (!isShell) continue;

    if (isRootView || currentIsOperationShell) {
      pushOperation(account);
      continue;
    }

    if (entityId && String(account.entityId || "").trim() === entityId) {
      pushOperation(account);
    }
  }

  // Root detail: always surface the current Root row (fresh detail balances).
  if (isRootView && currentId) {
    const existing = operationById.get(currentId);
    pushOperation(
      {
        ...(existing || {}),
        ...currentAccount,
        id: currentId,
        role: currentAccount?.role || existing?.role || "root",
        balances: mergeBalanceLists(
          currentAccount?.balances,
          existing?.balances,
        ),
      },
      { allowCurrent: true },
    );
  }

  const byRoleThenName = (a, b) => {
    const aRole = String(a?.role || a?.type || "")
      .trim()
      .toLowerCase();
    const bRole = String(b?.role || b?.type || "")
      .trim()
      .toLowerCase();
    const aOrder = RELATED_OPERATION_SHELL_ROLE_ORDER[aRole] ?? 50;
    const bOrder = RELATED_OPERATION_SHELL_ROLE_ORDER[bRole] ?? 50;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return String(a.name || a.alias || a.id).localeCompare(
      String(b.name || b.alias || b.id),
    );
  };

  const byName = (a, b) =>
    String(a.name || a.alias || a.id).localeCompare(String(b.name || b.alias || b.id));

  return {
    operationAccounts: Array.from(operationById.values()).sort(byRoleThenName),
    virtualAccounts: Array.from(virtualById.values()).sort(byName),
  };
};

/**
 * Fill missing shell balances via GET …/balances (transfer-options / list may omit them).
 */
export const enrichRelatedAccountsWithBalances = async (instance, accounts = []) => {
  if (!instance || !Array.isArray(accounts) || accounts.length === 0) {
    return Array.isArray(accounts) ? accounts : [];
  }

  return Promise.all(
    accounts.map(async (account) => {
      const id = String(account?.id || "").trim();
      if (!id || (account?.balances || []).length > 0) return account;
      const balances = await fetchAccountBalances(instance, id).catch(() => []);
      if (!balances.length) return account;
      return { ...account, balances };
    }),
  );
};

/** @deprecated Use fetchBaasAccounts */
export const getMockBaasAccounts = (instanceId) => {
  void instanceId;
  return [];
};

export const getBaasAccountEntities = async (instance, options = {}) => {
  const ownershipDocument = String(options.ownershipDocument || "").trim();
  const entities = await fetchBaasEntities(instance, {
    ownershipDocument: ownershipDocument || undefined,
  }).catch(() => []);
  return entities
    .map((entity) => ({
      id: entity.id,
      name: entity.name,
      document: entity.document || "",
      type: entity.type || "",
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/** @deprecated Prefer fetchBaasAccountById */
export const getBaasAccountById = (accountId, instanceId) => {
  void instanceId;
  return accountId ? { id: accountId, balances: [], metadata: {} } : null;
};

/**
 * Opens an account via BaaS `POST /v1/accounts`.
 * OpenAPI OpenAccount accepts entity_id + country_code (+ optional account_type CNR|WALLET|OTC).
 */
export const createBaasAccount = async (instance, form = {}) => {
  if (!instance) {
    throw new Error("Instance is required");
  }

  const entityId = form.entityId || form.entity_id;
  if (!entityId) {
    throw new Error("entityId is required");
  }

  const currencies = (Array.isArray(form.currencies) ? form.currencies : [])
    .map((code) => String(code || "").toUpperCase())
    .filter(Boolean);
  const primaryCurrency = currencies[0] || "BRL";
  const countryCode =
    form.countryCode ||
    form.country_code ||
    (primaryCurrency === "USD" ? "US" : "BR");

  const uiType = String(form.type || form.accountType || form.account_type || "")
    .trim()
    .toLowerCase();
  const body = {
    entity_id: entityId,
    country_code: countryCode,
  };
  if (uiType === "otc" || uiType === "otc_virtual") {
    body.account_type = "OTC";
    body.country_code = "BR";
  } else if (isBaasVirtualAccountType(uiType)) {
    body.account_type = "WALLET";
    body.country_code = "BR";
  }

  const created = await baasProxy({
    instance,
    method: "POST",
    baasPath: "v1/accounts",
    body,
  });

  const balances = created?.banking?.account_id
    ? await fetchAccountBalances(instance, created.banking.account_id)
    : [];

  return mapBaasAccountToUi(created, {
    entityName: form.entityName || "",
    instanceId: instance?.id,
    balances,
  });
};

/**
 * Re-send account-opened (welcome) e-mail via Baas
 * `POST v1/accounts/{account_id}/welcome-email/resend` (scope accounts.create).
 */
export const resendBaasAccountWelcomeEmail = async (instance, accountId) => {
  if (!instance || !accountId) {
    const err = new Error("instance and accountId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  return baasProxy({
    instance,
    method: "POST",
    baasPath: `v1/accounts/${encodeURIComponent(accountId)}/welcome-email/resend`,
  });
};

export const patchBaasAccountLifecycle = async (
  instance,
  accountId,
  { action, retainBalance = false } = {},
) => {
  if (!instance || !accountId || !action) {
    const err = new Error("instance, accountId and action are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  return baasProxy({
    instance,
    method: "PATCH",
    baasPath: `v1/accounts/${encodeURIComponent(accountId)}/lifecycle`,
    body: {
      action: String(action).trim().toLowerCase(),
      retain_balance: Boolean(retainBalance),
    },
  });
};

/** @deprecated Use createBaasAccount */
export const createMockBaasAccount = async (instanceIdOrInstance, form = {}) => {
  const instance =
    typeof instanceIdOrInstance === "object"
      ? instanceIdOrInstance
      : { id: instanceIdOrInstance, baasInstanceId: null, environment: null };

  if (!instance?.baasInstanceId && typeof instanceIdOrInstance === "string") {
    // Legacy callers passed selection id only — require full instance object for proxy.
    throw new Error("createBaasAccount requires the selected instance object");
  }

  return createBaasAccount(instance, form);
};

export const getPrimaryBalance = (balances = []) => {
  if (!balances.length) return null;
  const brl = balances.find((balance) => balance.currency === "BRL");
  if (brl) return brl;
  return balances[0];
};

export const hasBaasOverBalance = (balance) =>
  Boolean(balance?.allowOverdraft) || Number(balance?.limit) > 0;

/** Console lists: raw Disponível, plus Over Balance when the customer has a limit. */
export const formatBaasAvailableWithLimit = (balance) => {
  if (!balance) return null;
  const available = formatMonetaryValue(balance.available, balance.currency);
  if (!hasBaasOverBalance(balance)) return available;
  return `${available} · ${intl.get("extract.overdraftLimit") || "Over Balance"} ${formatMonetaryValue(
    Number(balance.limit) || 0,
    balance.currency,
  )}`;
};

export const getAdditionalCurrencyCount = (balances = []) =>
  Math.max(0, balances.length - 1);

/**
 * Conta Digital proposal timeline from inbound processor `evento_conta`.
 * `GET /v1/accounts/{accountId}/opening-events` — CPF already masked by the API.
 */
export const fetchBaasAccountOpeningEvents = async (instance, accountId) => {
  if (!instance || !accountId) return { events: [] };

  try {
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: `v1/accounts/${encodeURIComponent(accountId)}/opening-events`,
    });
    const events = Array.isArray(data?.events) ? data.events : [];
    return {
      accountId: data?.account_id || data?.accountId || accountId,
      proposalId: data?.proposal_id ?? data?.proposalId ?? null,
      events: events.map((event) => ({
        occurredAt: event?.occurred_at || event?.occurredAt || null,
        event: event?.event || "",
        status: event?.status || null,
        analysisType: event?.analysis_type || event?.analysisType || null,
        proposalId: event?.proposal_id ?? event?.proposalId ?? null,
        document: event?.document || null,
        partnerAccountId: event?.account_id ?? event?.partnerAccountId ?? null,
        accountNumber: event?.account_number || event?.accountNumber || null,
        branch: event?.branch || null,
      })),
    };
  } catch {
    return { events: [] };
  }
};
